<template>
  <div
    class="xray-strip"
    ref="container"
    @wheel.prevent="onWheel"
    @mousedown.right="onPanStart"
    @mousemove="onPanMove"
    @mouseup.right="onPanEnd"
    @mouseleave="onPanEnd"
    @contextmenu.prevent
  >
    <div class="strip-layer" :style="layerStyle">
      <div v-for="(img, idx) in images" :key="img ? img.id : idx" class="strip-image-slot" :style="slotStyle(img, idx)">
        <img
          v-if="img"
          :src="imageReformat(img.url)"
          :style="{ width: img.width + 'px', height: img.height + 'px', display: 'block' }"
          draggable="false"
        />
      </div>
    </div>
    <div
      v-for="anno in annotations"
      :key="anno.id"
      class="strip-anno-box"
      :class="{ selected: anno.id === selectedId, matched: !!anno.groupNo }"
      :style="getAnnoStyle(anno)"
      @click.stop="handleAnnoClick(anno)"
    >
      <span
        v-if="anno.groupNo"
        class="strip-group-label"
        :style="{ background: anno.id === selectedId ? selectedColor : color }"
        >{{ anno.groupNo }}</span
      >
    </div>
  </div>
</template>

<script>
import { imageReformat } from '@/utils/util';

export default {
  name: 'XrayStrip',
  props: {
    prevImage: Object,
    xrayImage: Object,
    nextImage: Object,
    prevOffsetY: { type: Number, default: 0 },
    currentOffsetY: { type: Number, default: 0 },
    nextOffsetY: { type: Number, default: 0 },
    annotations: Array,
    selectedId: Number,
    color: { type: String, default: '#5d89c0' },
    selectedColor: { type: String, default: '#ff6b6b' },
    disableAnnoIds: Array,
  },
  data() {
    return {
      fitScale: 1,
      zoom: 1,
      panX: 0,
      panY: 0,
      panning: false,
      panStartX: 0,
      panStartY: 0,
      panOriginX: 0,
      panOriginY: 0,
      contW: 0,
      contH: 0,
    };
  },
  computed: {
    images() {
      return [this.prevImage, this.xrayImage, this.nextImage];
    },
    offsets() {
      return [this.prevOffsetY, this.currentOffsetY, this.nextOffsetY];
    },
    minY() {
      return Math.min(...this.offsets);
    },
    totalWidth() {
      let w = 0;
      for (const img of this.images) {
        if (img) w += img.width;
      }
      return w;
    },
    totalHeight() {
      let maxB = 0;
      for (let i = 0; i < 3; i++) {
        const img = this.images[i];
        if (!img) continue;
        const b = this.offsets[i] + img.height;
        if (b > maxB) maxB = b;
      }
      return maxB - this.minY;
    },
    layerStyle() {
      const s = this.fitScale * this.zoom;
      return {
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: this.totalWidth + 'px',
        height: this.totalHeight + 'px',
        transform: `translate(calc(-50% + ${this.panX}px), calc(-50% + ${this.panY}px)) scale(${s})`,
        transformOrigin: 'center center',
      };
    },
  },
  mounted() {
    this.updateSize();
    this.$nextTick(() => this.updateSize());
    window.addEventListener('resize', this.updateSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateSize);
  },
  watch: {
    prevImage() {
      this.resetView();
    },
    xrayImage() {
      this.resetView();
    },
    nextImage() {
      this.resetView();
    },
  },
  methods: {
    imageReformat,
    updateSize() {
      const el = this.$refs.container;
      if (!el || !this.totalWidth || !this.totalHeight) return;
      this.contW = el.clientWidth;
      this.contH = el.clientHeight;
      this.fitScale = Math.min(this.contW / this.totalWidth, this.contH / this.totalHeight);
    },
    cumWidth(idx) {
      let w = 0;
      for (let i = 0; i < idx; i++) {
        const img = this.images[i];
        if (img) w += img.width;
      }
      return w;
    },
    slotStyle(img, idx) {
      if (!img) return { display: 'none' };
      const x = this.cumWidth(idx);
      const y = this.offsets[idx] - this.minY;
      return {
        position: 'absolute',
        left: x + 'px',
        top: y + 'px',
        width: img.width + 'px',
        height: img.height + 'px',
      };
    },
    getAnnoStyle(anno) {
      if (!this.xrayImage) return {};
      const s = this.fitScale * this.zoom;
      let [x1, y1, x2, y2] = anno.points;
      if (x1 > x2) [x1, x2] = [x2, x1];
      if (y1 > y2) [y1, y2] = [y2, y1];
      const stripX = this.cumWidth(1) + x1;
      const stripY = this.currentOffsetY - this.minY + y1;
      const halfW = this.contW / 2;
      const halfH = this.contH / 2;
      const color = anno.id === this.selectedId ? this.selectedColor : this.color;
      return {
        position: 'absolute',
        left: halfW + this.panX + (stripX - this.totalWidth / 2) * s + 'px',
        top: halfH + this.panY + (stripY - this.totalHeight / 2) * s + 'px',
        width: (x2 - x1) * s + 'px',
        height: (y2 - y1) * s + 'px',
        borderColor: color,
        '--box-bg': color + '26',
      };
    },
    handleAnnoClick(anno) {
      if (anno.groupNo) return;
      if (this.disableAnnoIds && this.disableAnnoIds.includes(anno.id)) return;
      this.$emit('annoClick', anno.id);
    },
    onWheel(e) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      this.zoom = Math.max(1, Math.min(20, this.zoom + delta));
    },
    onPanStart(e) {
      this.panning = true;
      this.panStartX = e.clientX;
      this.panStartY = e.clientY;
      this.panOriginX = this.panX;
      this.panOriginY = this.panY;
    },
    onPanMove(e) {
      if (!this.panning) return;
      this.panX = this.panOriginX + (e.clientX - this.panStartX);
      this.panY = this.panOriginY + (e.clientY - this.panStartY);
    },
    onPanEnd() {
      this.panning = false;
    },
    resetView() {
      this.zoom = 1;
      this.panX = 0;
      this.panY = 0;
      this.$nextTick(() => this.updateSize());
    },
  },
};
</script>

<style scoped>
.xray-strip {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  background: #e8e8e8;
}
.strip-image-slot {
  position: absolute;
}
.strip-image-slot > img {
  z-index: 1;
}
.strip-anno-box {
  position: absolute;
  border: 3px solid;
  border-radius: 2px;
  cursor: pointer;
  z-index: 2;
  box-sizing: border-box;
}
.strip-anno-box:hover {
  background: var(--box-bg, transparent);
}
.strip-anno-box.selected {
  background: rgba(255, 107, 107, 0.08);
}
.strip-anno-box.matched {
  opacity: 0.6;
  cursor: default;
}
.strip-group-label {
  position: absolute;
  top: -1px;
  right: -1px;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
  min-width: 20px;
  text-align: center;
  border-radius: 0 2px 0 2px;
  padding: 0 5px;
  pointer-events: none;
}
</style>
